import { httpsCallable } from 'firebase/functions';

import { auth } from './firebase';
import { functions } from './firebase';

// API Base URL - your Cloudflare Worker
const API_BASE_URL = 'https://api.microfb.com';

// Error handling
export class APIError extends Error {
  constructor(message: string, public code: string, public originalError?: unknown) {
    super(message);
    this.name = 'APIError';
  }
}

// Helper function to make authenticated API requests to Cloudflare Worker
async function makeAPIRequest<TResponse>(
  endpoint: string,
  options: RequestInit = {}
): Promise<TResponse> {
  try {
    // Get the current user's ID token for authentication
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new APIError('Authentication required', 'unauthenticated');
    }

    const idToken = await currentUser.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new APIError(
        errorText || `Request failed with status ${response.status}`,
        response.status.toString()
      );
    }

    return await response.json();
  } catch (error: any) {
    if (error instanceof APIError) {
      throw error;
    }
    
    // Handle network errors
    throw new APIError(
      error.message || 'Network request failed',
      'network-error',
      error
    );
  }
}

// Helper function to call Firebase Functions
async function callFunction<TRequest, TResponse>(
  functionName: string,
  data: TRequest
): Promise<TResponse> {
  try {
    const callable = httpsCallable<TRequest, TResponse>(functions, functionName);
    const result = await callable(data);
    return result.data;
  } catch (error: any) {
    // Handle Firebase Functions errors
    if (error.code === 'functions/unauthenticated') {
      throw new APIError('Authentication required', 'unauthenticated', error);
    }
    
    if (error.code === 'functions/permission-denied') {
      throw new APIError('Permission denied', 'permission-denied', error);
    }
    
    if (error.code === 'functions/not-found') {
      throw new APIError('Resource not found', 'not-found', error);
    }
    
    if (error.code === 'functions/already-exists') {
      throw new APIError('Resource already exists', 'already-exists', error);
    }

    // Extract error message from Firebase Functions error
    const message = error.message || 'An error occurred';
    throw new APIError(message, error.code || 'unknown', error);
  }
}

// Request/Response interfaces
export interface GetStatsRequest {
  siteId: string;
  from: string;
  to: string;
}

export interface StatsResponse {
  totalVotes: number;
  upVotes: number;
  downVotes: number;
  conversionRate: number;
  timeSeries: Array<{
    period: string;      // Firebase returns 'period', not 'date'
    votes: number;       // Firebase returns 'votes', not 'total'
    upVotes: number;
    downVotes: number;
  }>;
  countries: Record<string, number>;
  regions: Record<string, number>;
  cities: Record<string, number>;
  pages: Record<string, number>;
  comments: Array<{
    id: string;
    vote: 'up' | 'down';
    comment: string;     // Firebase returns 'comment', not 'message'
    timestamp: string;
    page: string;
  }>;
}

export interface SiteData {
  id: string;
  name: string;
  domain: string;
  plan: 'free' | 'pro' | 'enterprise';
  monthlyCount: number;
  totalVotes: number;
  createdAt: string;
  updatedAt: string;
  theme?: string;
  allowComments?: boolean;
  showVoteCounts?: boolean;
  emailAlerts?: boolean;
  alertThreshold?: number;
  // Stripe-related fields
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: 'active' | 'past_due' | 'canceled' | 'unpaid' | 'trialing';
  subscriptionCreatedAt?: string;
  subscriptionCanceledAt?: string;
  lastPaymentAt?: string;
  lastFailedPaymentAt?: string;
}

export interface CreateSiteRequest {
  domain: string;
  name?: string;
}

export interface CreateSiteResponse {
  success: boolean;
  siteId: string;
  message: string;
}

export interface CreateDefaultSiteResponse {
  success: boolean;
  siteId: string;
  message: string;
}

export interface UpdateSiteSettingsRequest {
  siteId: string;
  settings: {
    theme?: 'light' | 'dark';
    allowComments?: boolean;
    showVoteCounts?: boolean;
    emailAlerts?: boolean;
    alertThreshold?: number;
    name?: string;
  };
}

export interface UpdateSiteSettingsResponse {
  success: boolean;
  message: string;
}

export interface ExportCsvRequest {
  siteId: string;
  from: string; // ISO date string
  to: string;   // ISO date string
}

export interface ExportCsvResponse {
  blob: Blob;
  filename: string;
}

export interface PlanStatusResponse {
  plan: 'free' | 'pro' | 'enterprise';
  usage: number;
  limit: number;
  resetDate: string;
  subscriptionStatus?: 'active' | 'past_due' | 'canceled' | 'unpaid' | 'trialing';
}

export interface DeleteSiteRequest {
  siteId: string;
  confirmDeletion: boolean;
}

export interface DeleteSiteResponse {
  success: boolean;
  message: string;
  deletedCollections: string[];
  deletedDocuments: number;
}

export interface DeleteUserRequest {
  confirmDeletion: boolean;
  confirmText: string;
}

export interface DeleteUserResponse {
  success: boolean;
  message: string;
  deletedCollections: string[];
  deletedSites: string[];
  deletedDocuments: number;
}

export interface BackfillStatsResponse {
  diagnostic: {
    totalUnprocessedVotes: number;
    totalUnprocessedComments: number;
    oldestVoteDate: string | null;
    newestVoteDate: string | null;
    siteIds: string[];
  };
  processed: {
    statsCreated: number;
    votesProcessed: number;
    commentsProcessed: number;
  };
}

// Utility functions
export const getDateRange = (_period: 'week' | 'month' | 'year'): { from: string; to: string } => {
  const now = new Date();
  
  // Always use last 30 days for better performance and data liveliness
  // Round to end of day for stability
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  
  // Always go back 30 days regardless of period parameter
  const from = new Date(to);
  from.setDate(to.getDate() - 30);
  
  // Set from to start of day
  from.setHours(0, 0, 0, 0);
  
  /*
  console.log('📅 Date range calculated:', {
    period,
    from: from.toISOString(),
    to: to.toISOString(),
    daysDiff: Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
  });
  */
  
  return { from: from.toISOString(), to: to.toISOString() };
};

// API functions
export interface RollUpAllResponse {
  success: true;
  executedAt: string;
}

export const api = {
  // Get analytics stats for a site (goes through Cloudflare Worker)
  getStats: async (params: GetStatsRequest): Promise<StatsResponse> => {
    //console.log('🚀 API: Making getStats request with params:', params);
    
    try {
      const response = await makeAPIRequest<StatsResponse>('/getStats', {
        method: 'POST',
        body: JSON.stringify(params),
      });
      
      /*
      console.log('✅ API: getStats response received:', {
        totalVotes: response.totalVotes,
        upVotes: response.upVotes,
        downVotes: response.downVotes,
        conversionRate: response.conversionRate,
        timeSeriesLength: response.timeSeries?.length || 0,
        timeSeries: response.timeSeries,
        countriesCount: Object.keys(response.countries || {}).length,
        countries: response.countries,
        regionsCount: Object.keys(response.regions || {}).length,
        regions: response.regions,
        citiesCount: Object.keys(response.cities || {}).length,
        cities: response.cities,
        pagesCount: Object.keys(response.pages || {}).length,
        pages: response.pages,
        commentsCount: response.comments?.length || 0,
        fullResponse: response
      });
      */
      
      return response;
    } catch (error) {
      console.error('❌ API: getStats request failed:', error);
      throw error;
    }
  },

  // Backfill stats from all unprocessed votes (Firebase callable function)
  backfillStats: async (): Promise<BackfillStatsResponse> => {
    return callFunction<Record<string, never>, BackfillStatsResponse>('backfillStats', {});
  },

  rollUpAll: async (): Promise<RollUpAllResponse> => {
    return callFunction<Record<string, never>, RollUpAllResponse>('rollUpAllManual', {});
  },

  // Get plan status and usage (Firebase callable function)
  getPlanStatus: async (siteId: string): Promise<PlanStatusResponse> => {
    return callFunction<{ siteId: string }, PlanStatusResponse>('planStatus', { siteId });
  },

  // Delete a site (GDPR) (Firebase callable function)
  deleteSite: async (params: DeleteSiteRequest): Promise<DeleteSiteResponse> => {
    return callFunction<DeleteSiteRequest, DeleteSiteResponse>('gdprDeleteSite', params);
  },

  // Delete user account and all data (GDPR) (Firebase callable function)
  deleteUser: async (params: DeleteUserRequest): Promise<DeleteUserResponse> => {
    return callFunction<DeleteUserRequest, DeleteUserResponse>('gdprDeleteUser', params);
  },

  // Get current user's sites (Firebase callable function)
  getUserSites: async (): Promise<SiteData[]> => {
    return callFunction<Record<string, never>, SiteData[]>('getUserSites', {});
  },

  // Create a new site (Firebase callable function)
  createSite: async (params: CreateSiteRequest): Promise<CreateSiteResponse> => {
    return callFunction<CreateSiteRequest, CreateSiteResponse>('createSite', params);
  },

  // Create default site for new user (Firebase callable function)
  createDefaultSite: async (): Promise<CreateDefaultSiteResponse> => {
    return callFunction<Record<string, never>, CreateDefaultSiteResponse>('createDefaultSite', {});
  },

  // Update site settings (Firebase callable function)
  updateSiteSettings: async (params: UpdateSiteSettingsRequest): Promise<UpdateSiteSettingsResponse> => {
    return callFunction<UpdateSiteSettingsRequest, UpdateSiteSettingsResponse>('updateSiteSettings', params);
  },

  // Export data as CSV (goes through Cloudflare Worker)
  exportCsv: async (params: ExportCsvRequest): Promise<ExportCsvResponse> => {
    const url = new URL('/exportCsv', import.meta.env.VITE_API_BASE_URL);
    url.searchParams.set('siteId', params.siteId);
    url.searchParams.set('from', params.from);
    url.searchParams.set('to', params.to);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        // Try to parse error response as JSON first
        let errorMessage = `Export failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = await response.text() || errorMessage;
        }
        
        throw new APIError(errorMessage, response.status.toString());
      }

      // Get the blob and filename from response
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `export-${params.siteId}-${new Date().toISOString().split('T')[0]}.csv`;
      
      // Extract filename from Content-Disposition header if available
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      return { blob, filename };
    } catch (error: any) {
      if (error instanceof APIError) {
        throw error;
      }
      
      throw new APIError(
        error.message || 'CSV export request failed',
        'csv-export-error',
        error
      );
    }
  },

  // Health check
  healthCheck: async (): Promise<{ status: string; timestamp: string }> => {
    try {
      const response = await fetch(
        import.meta.env.DEV 
          ? 'http://localhost:5001/microfeedback-465714/us-central1/health'
          : 'https://us-central1-microfeedback-465714.cloudfunctions.net/health'
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      throw new APIError(
        'Health check failed',
        'health-check-error',
        error
      );
    }
  },
}; 

// Stripe API functions
export const stripeApi = {
  // Create checkout session
  createCheckoutSession: async (data: {
    siteId: string;
    priceId: string;
    successUrl: string;
    cancelUrl: string;
    customerEmail?: string;
  }) => {
    return makeAPIRequest('/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Create customer portal session
  createPortalSession: async (data: {
    customerId: string;
    returnUrl?: string;
  }) => {
    return makeAPIRequest('/create-portal-session', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get subscription details
  getSubscription: async (subscriptionId?: string, customerId?: string) => {
    const params = new URLSearchParams();
    if (subscriptionId) params.append('subscriptionId', subscriptionId);
    if (customerId) params.append('customerId', customerId);

    return makeAPIRequest(`/subscription?${params}`);
  },

  // Cancel subscription
  cancelSubscription: async (subscriptionId: string) => {
    return makeAPIRequest('/cancel-subscription', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId }),
    });
  },

  // Get invoices
  getInvoices: async (customerId: string, limit: number = 10) => {
    const params = new URLSearchParams({
      customerId,
      limit: limit.toString(),
    });

    return makeAPIRequest(`/invoices?${params}`);
  },
}; 